import { useCallback, useState } from 'react';
import _ from 'lodash';
import { AddDocumentation, AddDocumentsButton, FileList } from 'components';
import { DisputeStatus, DisputeType } from 'enums';
import { useBackupDocument, useDisputeDocument } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import { useDisputeDetails, useDotDocuments, useInvoice, useSupplierDocuments, useUserInfo } from 'state';
import { AttachedDocument } from 'types';
import styles from './DisputeDocuments.module.scss';
import { useInvoiceDocuments } from 'state/invoiceDocuments';

// If the document url starts with the onbase prefix, we will treat it
// as an onbase document and use the backup document endpoint to fetch it, 
// otherwise we will use the dispute document endpoint.
const ONBASE_DOCUMENT_URL_PREFIX = '/api/onbase/documents/';

export const isOnBaseDocument = ({ documentUrl }: AttachedDocument) =>
    documentUrl?.toLowerCase().startsWith(ONBASE_DOCUMENT_URL_PREFIX) ?? false;

const DisputeDocuments = () => {
    const { disputeNumber, disputeStatusId, disputeType, invoiceId, vendorId, accountId } = useDisputeDetails();
    const supplierDocuments = useSupplierDocuments(disputeNumber);
    const dotDocuments = useDotDocuments(disputeNumber);
    const { isInternalUser } = useUserInfo();
    const isOpen = disputeStatusId !== DisputeStatus.Closed;

    // If the dispute type is invoice, we need to fetch the invoice documents.
    const invoice = useInvoice(accountId ?? '', vendorId ?? '', encodeURIComponent(invoiceId ?? ''));
    const invoiceDocuments = useInvoiceDocuments(accountId ?? '', vendorId ?? '', invoiceId ?? '', invoice?.voucherPoId ?? '');
    const backupDocumentsRaw = (disputeType === DisputeType.Invoice && typeof invoiceId === 'string')
        ? invoiceDocuments
        : [];

    return (
        <div className={styles.documentsContainer}>
            {disputeType === DisputeType.Invoice && (
            <UploadDocumentation
                disputeNumber={disputeNumber}
                documents={backupDocumentsRaw}
                isInternalUser={isInternalUser}
                isUploadAllowed={false}
                title="Invoice Backup Documentation"
                source="backup"
            />
            )}
            <UploadDocumentation
            disputeNumber={disputeNumber}
            documents={supplierDocuments}
            isInternalUser={isInternalUser}
            isUploadAllowed={isOpen && !isInternalUser}
            title="Dispute Documentation"
            source="dispute"
            />
            <UploadDocumentation
            disputeNumber={disputeNumber}
            documents={dotDocuments}
            isInternalUser={isInternalUser}
            isUploadAllowed={isOpen && isInternalUser}
            title="Dot Provided Documentation"
            source="dispute"
            />
        </div>
    );
};

type UploadDocumentationProps = {
    disputeNumber: string,
    documents: AttachedDocument[],
    isInternalUser: boolean,
    isUploadAllowed: boolean,
    title: string,
    source: 'backup' | 'dispute',
};


const UploadDocumentation = ({ disputeNumber, documents, isInternalUser, isUploadAllowed, title, source }: UploadDocumentationProps) => {
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const getDisputeDocument = useDisputeDocument(disputeNumber, true);
    const getUploadDocument = useBackupDocument();
    const getDocument = useCallback(async (document: AttachedDocument) => {
        if (source === 'backup' || isOnBaseDocument(document)) {
            await getUploadDocument(document);
            return;
        }

        await getDisputeDocument(document);
    }, [getDisputeDocument, getUploadDocument, source]);
    const showError = getUploadDocument.showError || getDisputeDocument.showError;
    const errorIdentifier = getUploadDocument.showError
        ? getUploadDocument.errorIdentifier
        : getDisputeDocument.errorIdentifier;
    const closeError = () => {
        getUploadDocument.setShowError(false);
        getDisputeDocument.setShowError(false);
    };
    
    return (
        <div className={styles.documents}>
            <div className={styles.documentsTitle}>
                <span>{title}</span>
            </div>
            <div>
                {isUploadAllowed &&
                    <AddDocumentsButton
                        onClick={() => setShowDialog(true)}
                    />
                }
            </div>
            <div>
                {_.isEmpty(documents) ? (
                    <div className={styles.emptyDocuments}>No documents available.</div>
                ) : (
                    <FileList
                        documents={documents}
                        onClick={getDocument}
                    />
                )}
            </div>
            <AddDocumentation
                show={showDialog}
                onClose={() => setShowDialog(false)}
                isDotProvided={isInternalUser}
            />
            {showError && <DocumentErrorDialog onClose={closeError} documentIdentifier={errorIdentifier} />}
        </div>
    );
};

export default DisputeDocuments;
